import {
  Row,
  Col,
  Button,
  message,
  Form,
  Input,
  Select,
  Checkbox,
} from "antd";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addNewUser, getAdminGroups, getAssignableGroups, getAllWhDivisions, getCities, getDarkStores } from "store/admin-slice/adminServicesSlice";
import { fetchWarehouses } from "store/stock-exchange/stockExchangeSlice";
import { VEHICLE_TYPE_OPTIONS } from "common/constants";
import { parseStringOrList } from "common/utils";
import { MappedWarehousesDivisions, pruneUnmappedWarehouses } from "./components/MappedWarehousesDivisions";

const ASSIGNABLE_ROLE_MIN_ID = 31;

const MESSAGE_KEY = "CREATE_ADMIN_USER";
export const AddNewUserPage = () => {
  const [selectedCity, setSelectedCity] = useState<number | null>(null);
  useDocumentTitle("Add New User");
  const { status, error, adminUserGroups, assignableGroups, darkStores, cities, allWhDivisions } = useAppSelector(
    (state) => state.adminServices
  );
  const { warehouses } = useAppSelector(
    (state) => state.stockExchangeServices
  );
  const sourceOptions = useAppSelector((state) =>
    parseStringOrList(state.app.initConfig?.source)
  );
  const designationOptions = useAppSelector((state) =>
    parseStringOrList(state.app.initConfig?.designation)
  );
  const shiftOptions = useAppSelector((state) =>
    parseStringOrList(state.app.initConfig?.shift)
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const filteredStores = darkStores.filter(store =>
    selectedCity ? store.cityId === selectedCity : true
  );

  const handleCityChange = (value: number) => {
    setSelectedCity(value);
    form.setFieldsValue({ mappedStores: [] });
    if (value) {
      dispatch(getDarkStores(value));
    }
  };

  const designationSelectOptions = useMemo(
    () => designationOptions.map((d) => ({ value: d, label: d })),
    [designationOptions]
  );

  const shiftSelectOptions = useMemo(
    () => shiftOptions.map((s) => ({ value: s, label: s })),
    [shiftOptions]
  );

  const assignableGroupIds = useMemo(
    () => new Set(assignableGroups.map(g => g.id)),
    [assignableGroups]
  );

  const groupSelectOptions = useMemo(
    () =>
      adminUserGroups
        .filter(g => g.id >= ASSIGNABLE_ROLE_MIN_ID)
        .map(g => ({
          value: g.id,
          label: g.name,
          disabled: !assignableGroupIds.has(g.id),
        })),
    [adminUserGroups, assignableGroupIds]
  );

  useEffect(() => {
    dispatch(fetchWarehouses());
    dispatch(getAllWhDivisions());
  }, []);

  useEffect(() => {
    dispatch(getAdminGroups());
    dispatch(getAssignableGroups());
    dispatch(getCities());
    return () => {
      message.destroy();
    };
  }, []);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Admin Users Added!",
        key: MESSAGE_KEY,
      });
      form.resetFields();
      navigate(-1);
    } else if (status === "saving") {
      message.loading({
        content: "Saving User",
        key: MESSAGE_KEY,
      });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  const [form] = Form.useForm();
  const onSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      // Every mapped warehouse must have at least one division; drop the ones
      // that don't and inform the user.
      const { mappedWh, mappedDivisions, removed } = pruneUnmappedWarehouses(
        values.mappedWh ?? [],
        values.mappedDivisions ?? [],
        warehouses,
        allWhDivisions
      );
      values.mappedWh = mappedWh;
      values.mappedDivisions = mappedDivisions;
      form.setFieldsValue({ mappedWh, mappedDivisions });
      if (removed.length > 0) {
        message.warning(
          `Removed warehouse(s) without a selected division: ${removed.join(", ")}`
        );
      }
      if (values.vehicleType == null) {
        delete values.vehicleType;
      }
      if (values.source == null) {
        delete values.source;
      }
      if (values.designation == null) {
        delete values.designation;
      }
      if (values.shift == null) {
        delete values.shift;
      }
      if (values.enableLmPerformanceBasedPayout == null) {
        delete values.enableLmPerformanceBasedPayout;
      } else {
        // Select holds "true" / "false" strings so False stays a selectable value.
        values.enableLmPerformanceBasedPayout =
          values.enableLmPerformanceBasedPayout === "true";
      }
      await dispatch(addNewUser(values));
    }
    catch (e) {
      //Place logger here
    }
  };

  const onCancelSave = () => {
    navigate(-1);
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Add New User" />
        </Col>
      </Row>
      <Form
        form={form}
        name="vendor-filter-form"
        layout="vertical"
        style={{ width: "50%" }}
      >
        <Form.Item name="firstname" label="First Name" rules={[
          {
            required: true,
          },
        ]}>
          <Input placeholder="First Name" size="small" allowClear required />
        </Form.Item>
        <Form.Item name="lastname" label="Last Name" rules={[
          {
            required: true,
          },
        ]}>
          <Input placeholder="Last Name" size="small" allowClear required />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[
          {
            required: true,
            type: "email",
            message: "Please enter a valid email",
          },
        ]}>
          <Input placeholder="Email" size="small" allowClear required />
        </Form.Item>
        <Form.Item name="phone" label="Phone" rules={[
          {
            pattern: new RegExp("^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$"),
            message: "Invalid phone number",
            min: 10,
            max: 10,
            required: true,
          },
        ]} >
          <Input
            placeholder="Phone" 
            size="middle" 
            maxLength={10} 
            allowClear 
          />
        </Form.Item>
        <Form.Item
          label="Gender"
          name="gender"
          required
        >
          <Select
            defaultValue="MALE"
            options={[
              {
                value: 'MALE',
                label: 'Male',
              }, {
                value: 'FEMALE',
                label: 'Female',
              },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Vehicle Type"
          name="vehicleType"
        >
          <Select
            placeholder="Select Vehicle Type"
            allowClear
            options={VEHICLE_TYPE_OPTIONS}
          />
        </Form.Item>

        <Form.Item
          label="Source"
          name="source"
        >
          <Select
            placeholder="Select Source"
            allowClear
            options={sourceOptions.map((s) => ({
              value: s,
              label: s,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Designation"
          name="designation"
        >
          <Select
            placeholder="Select Designation"
            allowClear
            options={designationSelectOptions}
          />
        </Form.Item>

        <Form.Item
          label="Shift"
          name="shift"
        >
          <Select
            placeholder="Select Shift"
            allowClear
            options={shiftSelectOptions}
          />
        </Form.Item>

        <Form.Item
          label="LM Performance Based Payout"
          name="enableLmPerformanceBasedPayout"
        >
          <Select
            placeholder="Select True / False"
            allowClear
            options={[
              { value: "true", label: "True" },
              { value: "false", label: "False" },
            ]}
          />
        </Form.Item>

        <MappedWarehousesDivisions
          form={form}
          warehouses={warehouses}
          divisions={allWhDivisions}
        />

        <Form.Item
          label="City"
        >
          <Select
            value={selectedCity}
            onChange={handleCityChange}
            options={cities.map(city => ({
              value: city.id,
              label: `${city.name}, ${city.state}`,
            }))}
            allowClear
            placeholder="Select city to filter stores"
          />
        </Form.Item>

        <Form.Item
          label="Mapped Stores"
          name="mappedStores"
        >
          <Select
            mode="multiple"
            options={filteredStores.map(store => ({
              label: `${store.id} || ${store.name}`,
              value: store.id,
            }))}
          />
        </Form.Item>

        <Form.Item name="hasToBeGigWorker" valuePropName="checked">
          <Checkbox>Has To Be Gig Worker</Checkbox>
        </Form.Item>
        
        <Form.Item name="groups" label="Groups">
          <Select
            mode="multiple"
            showSearch
            placeholder="Select roles"
            filterOption={(input, option) =>
              String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={groupSelectOptions}
          />
        </Form.Item>
      </Form>
      <Row>
        <Col>
          <Button
            type="primary"
            onClick={onSave}
            loading={status === "loading"}
            disabled={status === "loading"}
          >
            Add User
          </Button>
        </Col>
        <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
          <Button
            onClick={onCancelSave}
            disabled={status === "loading"}
          >
            Cancel
          </Button>
        </Col>
      </Row>
    </>
  );
};